// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-01-16 12:53:50 +0100 (Fri, 16 Jan 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 159848 $
//  Purpose     : call i18n apis
// =============================================================================
// ---Third-party imports
import {put, takeEvery, call} from 'redux-saga/effects'

// ---Framework imports
import {get, size} from 'ldash'
import {selectInitCacheKeys} from 'uix/selectors'
import {select, recoveredCachedConfigs} from 'uix/sagas'
import Api from 'uix/api'
import {setLanguage, updateBaseLanguage, reloadLanguage} from './actions'
import log from 'uix/logging'
import {mergeI18nLayers} from './utils'

// ---Globals
function* applyI18nData({ddata, language, dname, baseI18n}) {
  let k = `i18n_${language}`
  let newCk = {[k]: ddata.cacheKeys[k]}
  let domainI18n = ddata.i18n || {}
  let baseBundle = ddata.i18n_global || {}
  let nextBase = baseI18n
  if (baseBundle && size(baseBundle) > 0) {
    nextBase = {...baseI18n, ...baseBundle}
    yield put(updateBaseLanguage(baseBundle))
  }
  let merged = mergeI18nLayers(nextBase, domainI18n)
  yield put(
    setLanguage({
      language,
      dname,
      i18ndata: domainI18n,
      cacheKeys: newCk,
      merged,
    })
  )
}

function* fetchI18nData({language, dname, cacheKeys, baseI18n}) {
  let baseLoaded = !!baseI18n[language]
  let params = {language}
  if (cacheKeys) {
    params.cacheKeys = JSON.stringify(cacheKeys)
  }
  if (!baseLoaded) {
    params.globalbase = true
  }
  let {data: ddata, error} = yield call(Api.post, '/api/loadi18n', params, {dname})
  if (error || !ddata) {
    log.warn('i18n fetch failed: %o', error || 'no data')
    return null
  }
  return ddata
}

function* switchLanguage({payload}) {
  let language = payload.language
  let root = yield select()
  let cacheKeys = selectInitCacheKeys(root)

  let dname = root.meta.request.dname
  let baseI18n = root.config.instance.i18n || {}
  let baseLoaded = !!baseI18n[language]
  let domainCached = get(root.config.ddata[dname].i18n, language)
  let shouldFetch = !domainCached || !baseLoaded

  if (shouldFetch) {
    let ddata = yield fetchI18nData({language, dname, cacheKeys, baseI18n})
    if (ddata) {
      ddata = recoveredCachedConfigs(root.config.ddataCache, ddata)
      yield applyI18nData({ddata, language, dname, baseI18n})
    }
  } else {
    let merged = mergeI18nLayers(baseI18n, {[language]: domainCached})
    yield put(setLanguage({language, dname, i18ndata: {[language]: domainCached}, merged}))
  }

  // notify server session
  yield call(Api.post, '/api/changeLanguage', {lang: language})
}

function* reloadLanguageSaga({payload}) {
  let {language, dname} = payload
  let root = yield select()
  if (!dname) {
    dname = root.meta.request.dname
  }
  let baseI18n = root.config.instance.i18n || {}
  let ddata = yield fetchI18nData({language, dname, baseI18n})
  if (!ddata) {
    return
  }
  ddata = recoveredCachedConfigs(root.config.ddataCache, ddata)
  yield applyI18nData({ddata, language, dname, baseI18n})
}

export const sagas = {
  switchLanguage: takeEvery('SWITCH_LANGUAGE', switchLanguage),
  reloadLanguage: takeEvery('RELOAD_LANGUAGE', reloadLanguageSaga),
}
